import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePromptVariants from '../../../../hooks/promptVariant/usePromptVariants';
import PromptVariantCreateEditModal from './PromptVariantCreateEditModal';
import PromptVariantViewModal from './PromptVariantViewModal';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { serviceProtocolI18n } from './i18n';
import { useSnackbar } from '../../../../context/SnackbarContext';

const formatDateTime = (value, locale = 'es-CL') => {
  if (!value) return '--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function PromptVariantsPanel({
  Card,
  Button,
  Input,
  Table,
  Badge,
  IconButton,
  Modal,
  onBack,
}) {
  const { language } = useLanguage();
  const serviceProtocolLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: serviceProtocolI18n });
  const { showSnackbar } = useSnackbar();

  const {
    variants,
    loading,
    error,
    fetchVariants,
    fetchVariantById,
    submitVariant,
    removeVariant,
  } = usePromptVariants();

  const [showCreateEdit, setShowCreateEdit] = useState(false);
  const [editMode, setEditMode] = useState('create');
  const [editData, setEditData] = useState(null);
  const [showView, setShowView] = useState(false);
  const [viewVariant, setViewVariant] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    serviceProtocolI18n.changeLanguage(serviceProtocolLanguage);
  }, [serviceProtocolLanguage]);

  const currentLocale = useMemo(() => {
    if (serviceProtocolLanguage === 'enUS') return 'en-US';
    if (serviceProtocolLanguage === 'ptBR') return 'pt-BR';
    return 'es-CL';
  }, [serviceProtocolLanguage]);

  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  useEffect(() => {
    if (!error) return;
    showSnackbar(
      error.customMessage || error.message || t('service_protocol_prompt_error_load'),
      'error'
    );
  }, [error, showSnackbar, t]);

  const filteredVariants = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return variants;
    return variants.filter(
      (v) =>
        (v.name || '').toLowerCase().includes(q) ||
        (v.createdByName || '').toLowerCase().includes(q)
    );
  }, [variants, search]);

  const tableRows = useMemo(
    () =>
      filteredVariants.map((v) => ({
        id: v.id,
        name: v.name || '--',
        version: v.version ?? '--',
        createdBy: v.createdByName || v.createdBy || '--',
        createdAt: formatDateTime(v.createdAt, currentLocale),
        _raw: v,
      })),
    [filteredVariants, currentLocale]
  );

  // --- Handlers ---
  const handleCreate = useCallback(() => {
    setEditMode('create');
    setEditData(null);
    setShowCreateEdit(true);
  }, []);

  const handleView = useCallback(
    async (row) => {
      try {
        const detail = await fetchVariantById(row._raw.id);
        setViewVariant(detail);
        setShowView(true);
      } catch {
        // error handled by hook
      }
    },
    [fetchVariantById]
  );

  const handleEdit = useCallback(
    async (row) => {
      try {
        const detail = await fetchVariantById(row._raw.id);
        setEditMode('edit');
        setEditData(detail);
        setShowCreateEdit(true);
      } catch {
        // error handled by hook
      }
    },
    [fetchVariantById]
  );

  const handleSave = useCallback(
    async (payload) => {
      const result = await submitVariant(payload);
      if (result.ok) {
        setShowCreateEdit(false);
        setEditData(null);
        fetchVariants();
        showSnackbar(t('service_protocol_prompt_success_saved'), 'success');
      } else {
        const msg =
          result.error?.customMessage ||
          result.error?.message ||
          t('service_protocol_prompt_error_save');
        showSnackbar(msg, 'error');
      }
    },
    [submitVariant, fetchVariants, showSnackbar, t]
  );

  const handleDeleteClick = useCallback((row) => {
    setDeleteTarget(row._raw);
    setShowDelete(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    const result = await removeVariant(deleteTarget.id);
    if (result.ok) {
      setShowDelete(false);
      setDeleteTarget(null);
      fetchVariants();
      showSnackbar(t('service_protocol_prompt_success_deleted'), 'success');
    } else {
      const msg =
        result.error?.customMessage ||
        result.error?.message ||
        t('service_protocol_prompt_error_delete');
      showSnackbar(msg, 'error');
    }
  }, [deleteTarget, removeVariant, fetchVariants, showSnackbar, t]);

  // --- Table columns ---
  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        label: t('service_protocol_prompt_table_name'),
      },
      {
        accessor: 'version',
        label: t('service_protocol_prompt_table_version'),
        render: (row) =>
          Badge ? (
            <Badge text={`v${row.version}`} color='info' size='sm' />
          ) : (
            `v${row.version}`
          ),
      },
      { accessor: 'createdBy', label: t('service_protocol_prompt_table_creator') },
      {
        accessor: 'createdAt',
        label: t('service_protocol_prompt_table_created_at'),
      },
      {
        label: t('service_protocol_prompt_table_actions'),
        render: (row) => (
          <div className='d-flex align-items-center gap-2'>
            {IconButton ? (
              <>
                <IconButton
                  ariaLabel={t('service_protocol_action_view')}
                  icon='eye'
                  onClick={() => handleView(row)}
                  size='md'
                />
                <IconButton
                  ariaLabel={t('service_protocol_action_edit')}
                  icon='pencil'
                  onClick={() => handleEdit(row)}
                  size='md'
                  iconStroke='var(--primary-500)'
                />
                <IconButton
                  ariaLabel={t('service_protocol_action_delete')}
                  icon='trash'
                  onClick={() => handleDeleteClick(row)}
                  size='md'
                  iconStroke='var(--error-500)'
                />
              </>
            ) : (
              <>
                <button
                  type='button'
                  className='btn btn-outline-secondary btn-sm'
                  onClick={() => handleView(row)}
                >
                  {t('service_protocol_action_view')}
                </button>
                <button
                  type='button'
                  className='btn btn-outline-primary btn-sm'
                  onClick={() => handleEdit(row)}
                >
                  {t('service_protocol_action_edit')}
                </button>
                <button
                  type='button'
                  className='btn btn-outline-danger btn-sm'
                  onClick={() => handleDeleteClick(row)}
                >
                  {t('service_protocol_action_delete')}
                </button>
              </>
            )}
          </div>
        ),
      },
    ],
    [IconButton, Badge, handleView, handleEdit, handleDeleteClick, t]
  );

  // --- Loading state ---
  if (loading && variants.length === 0) {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ minHeight: 300 }}
      >
        <p>{t('service_protocol_prompt_loading')}</p>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className='d-flex justify-content-between align-items-start mb-3'>
        <div>
          <div className='d-flex align-items-center gap-2 mb-1'>
            {IconButton && (
              <IconButton
                ariaLabel={t('service_protocol_prompt_back')}
                icon='arrowLeft'
                onClick={onBack}
                size='md'
              />
            )}
            <h2 className='mb-0'>{t('service_protocol_prompt_title')}</h2>
          </div>
          <p className='caption font-light'>
            {t('service_protocol_prompt_subtitle')}
          </p>
        </div>
        <Button
          label={t('service_protocol_prompt_new')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={handleCreate}
        />
      </div>

      {/* Error */}
      {error && (
        <div
          className='mb-3'
          style={{
            background: 'var(--error-100, #fee2e2)',
            border: '1px solid var(--error-300, #fca5a5)',
            borderRadius: 8,
            padding: 12,
            color: 'var(--error-700, #b91c1c)',
            fontSize: '0.85rem',
          }}
        >
          {error.customMessage ||
            error.message ||
            t('service_protocol_prompt_error_load')}
        </div>
      )}

      {/* Search + Table */}
      <div
        className='p-3'
        style={{
          background: '#fff',
          border: '1px solid #e6e8eb',
          borderRadius: 12,
        }}
      >
        <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-3'>
          <h3 className='h6 m-0'>{t('service_protocol_prompt_created_title')}</h3>
          <div style={{ minWidth: 280, flex: '0 0 280px' }}>
            {Input ? (
              <Input
                icon='search'
                name='searchPrompt'
                placeholder={t('service_protocol_prompt_search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            ) : (
              <input
                className='form-control'
                placeholder={t('service_protocol_prompt_search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
          </div>
        </div>

        {Table ? (
          <Table
            columns={columns}
            data={tableRows}
            loading={loading}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
            ]}
          />
        ) : tableRows.length === 0 ? (
          <p className='caption font-light text-center py-4'>
            {t('service_protocol_prompt_empty')}
          </p>
        ) : (
          <table className='table'>
            <thead>
              <tr>
                <th>{t('service_protocol_prompt_table_name')}</th>
                <th>{t('service_protocol_prompt_table_version')}</th>
                <th>{t('service_protocol_prompt_table_creator')}</th>
                <th>{t('service_protocol_prompt_table_created_at')}</th>
                <th>{t('service_protocol_prompt_table_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>v{row.version}</td>
                  <td>{row.createdBy}</td>
                  <td>{row.createdAt}</td>
                  <td>
                    <div className='d-flex gap-1'>
                      <button
                        type='button'
                        className='btn btn-outline-secondary btn-sm'
                        onClick={() => handleView(row)}
                      >
                        {t('service_protocol_action_view')}
                      </button>
                      <button
                        type='button'
                        className='btn btn-outline-primary btn-sm'
                        onClick={() => handleEdit(row)}
                      >
                        {t('service_protocol_action_edit')}
                      </button>
                      <button
                        type='button'
                        className='btn btn-outline-danger btn-sm'
                        onClick={() => handleDeleteClick(row)}
                      >
                        {t('service_protocol_action_delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      <PromptVariantCreateEditModal
        Modal={Modal}
        Input={Input}
        Button={Button}
        Badge={Badge}
        show={showCreateEdit}
        mode={editMode}
        initialData={editData}
        onSave={handleSave}
        onClose={() => {
          setShowCreateEdit(false);
          setEditData(null);
        }}
      />

      {/* View Modal */}
      <PromptVariantViewModal
        Modal={Modal}
        Badge={Badge}
        show={showView}
        variant={viewVariant}
        onClose={() => {
          setShowView(false);
          setViewVariant(null);
        }}
      />

      {/* Delete Confirm */}
      <ConfirmModal
        Modal={Modal}
        show={showDelete}
        title={t('service_protocol_prompt_confirm_delete_title')}
        message={
          deleteTarget
            ? t('service_protocol_prompt_confirm_delete_message', {
                name: deleteTarget.name,
              })
            : t('service_protocol_prompt_confirm_delete_fallback')
        }
        confirmLabel={t('service_protocol_confirm_delete_button')}
        confirmVariant='danger'
        onConfirm={handleDeleteConfirm}
        onClose={() => {
          setShowDelete(false);
          setDeleteTarget(null);
        }}
      />
    </>
  );
}
